import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users, Buildings,
  CaretLeft, CaretRight, ChartBar, FolderOpen, ClockCounterClockwise, CreditCard, FileText, Receipt, List, X, ChartLine, Lifebuoy, EnvelopeSimple, PaperPlaneTilt,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { getPlan } from '../lib/planFeatures';

const NAV_ITEMS = {
  super_admin: [
    { label: 'Dashboard', icon: ChartBar, path: '/dashboard' },
    { label: 'Analytics', icon: ChartLine, path: '/analytics' },
    { label: 'Tenants', icon: Buildings, path: '/tenants' },
    { label: 'Billing', icon: CreditCard, path: '/billing' },
    { label: 'Invoices', icon: Receipt, path: '/invoices' },
    { label: 'Support', icon: Lifebuoy, path: '/admin/support' },
    { label: 'Inbox', icon: EnvelopeSimple, path: '/admin/contact-submissions' },
    { label: 'Broadcasts', icon: PaperPlaneTilt, path: '/admin/broadcasts' },
    { label: 'Reports', icon: FileText, path: '/billing-reports' },
    { label: 'Activity Log', icon: ClockCounterClockwise, path: '/audit-logs' },
  ],
  tenant_admin: [
    { label: 'Dashboard', icon: ChartBar, path: '/dashboard' },
    { label: 'Quick Look', icon: MagnifyingGlass, path: '/quick-look' },
    { label: 'Analytics', icon: ChartLine, path: '/analytics' },
    { label: 'Users', icon: Users, path: '/users' },
    { label: 'Projects', icon: FolderOpen, path: '/projects' },
    { label: 'Billing', icon: CreditCard, path: '/my-billing' },
    { label: 'Invoices', icon: Receipt, path: '/my-invoices' },
    { label: 'Support', icon: Lifebuoy, path: '/support' },
    { label: 'Activity Log', icon: ClockCounterClockwise, path: '/audit-logs' },
  ],
  regular_user: [
    { label: 'Dashboard', icon: ChartBar, path: '/dashboard' },
    { label: 'Quick Look', icon: MagnifyingGlass, path: '/quick-look' },
    { label: 'Analytics', icon: ChartLine, path: '/analytics' },
    { label: 'Projects', icon: FolderOpen, path: '/projects' },
  ],
};

export default function Sidebar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = user?.role || 'regular_user';
  const isLocked = !!user?.is_locked;
  const isCanceling = !!user?.cancel_at_period_end || ['canceled', 'canceling'].includes(user?.subscription_status);
  let items = NAV_ITEMS[role] || NAV_ITEMS.regular_user;
  // When the tenant is locked (pending_payment / trial_expired) OR has canceled their
  // subscription, hide data nav items so users can only reach Dashboard, Billing & Invoices.
  if ((isLocked || isCanceling) && role !== 'super_admin') {
    const allowed = new Set(['/dashboard', '/my-billing', '/billing', '/my-invoices', '/invoices']);
    items = items.filter(i => allowed.has(i.path));
  }

  // Hide Analytics for tenants on the Basic plan (Pro/Enterprise keep access).
  // Super admins always see Analytics.
  if (role !== 'super_admin' && getPlan(user) === 'basic') {
    items = items.filter(i => i.path !== '/analytics');
  }

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  function handleNav(path) {
    navigate(path);
    setMobileOpen(false);
  }

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex items-center justify-between px-5 h-16 shrink-0">
        <div className="cursor-pointer" onClick={() => handleNav('/dashboard')} data-testid="sidebar-logo">
          {collapsed ? (
            <img src="/logo-white.png" alt="RoofMate" className="h-7 object-contain shrink-0" />
          ) : (
            <img src="/logo-full-white.png" alt="RoofMate" className="h-7 object-contain" />
          )}
        </div>
        {/* Mobile close button */}
        <button onClick={() => setMobileOpen(false)} className="lg:hidden w-8 h-8 flex items-center justify-center text-white/50 hover:text-white">
          <X size={20} />
        </button>
      </div>

      {!collapsed && (
        <div className="px-6 pt-4 pb-2">
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/30">Main</span>
        </div>
      )}

      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {items.map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => handleNav(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-[10px] rounded-md text-[13px] font-medium transition-all duration-200 ${
                active ? 'bg-primary text-white shadow-md shadow-primary/30' : 'text-white/50 hover:text-white/80 hover:bg-white/[0.06]'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon size={20} weight={active ? 'fill' : 'regular'} className="shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Collapse Toggle - desktop only */}
      <div className="py-3 px-3 hidden lg:block">
        <button onClick={() => setCollapsed(!collapsed)} className="w-full flex items-center justify-center py-2 rounded-md text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-all" data-testid="sidebar-collapse-btn">
          {collapsed ? <CaretRight size={16} /> : <CaretLeft size={16} />}
        </button>
      </div>

      {!collapsed && user && (
        <div className="px-5 pb-4">
          <div className="flex items-center gap-3 p-3 rounded-md bg-white/[0.06]">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold shrink-0">
              {(user.name || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-white/80 truncate">{user.name || user.email}</div>
              <div className="text-[10px] text-white/30 capitalize">{role === 'super_admin' ? 'Admin' : role === 'tenant_admin' ? 'Tenant' : 'User'}</div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-3 left-3 z-50 w-10 h-10 rounded-md bg-card border border-border shadow-card flex items-center justify-center"
        data-testid="mobile-menu-btn"
      >
        <List size={20} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-[260px] flex flex-col transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: 'hsl(var(--sidebar-bg))' }}
        data-testid="mobile-sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex h-screen flex-col border-r border-border/50 transition-all duration-300 ease-out shrink-0 ${collapsed ? 'w-[72px]' : 'w-[260px]'}`}
        style={{ background: 'hsl(var(--sidebar-bg))' }}
        data-testid="app-sidebar"
      >
        {sidebarContent}
      </aside>
    </>
  );
}
